// Canvas draw helpers: Stardew-ish placeholder bodies (v0.1).
// Unique palette per NPC; facing via 4-dir offset; walk bob when moving.
// Bodies drawn small (~28x40) so 20 NPCs stay readable.
import type { Bed, Direction, Npc, RuntimeNpc } from '../types.js';
import { speakerAnchor } from '../sim/bubbles.js';

// Canvas extends past the walls so edge bubbles/labels never clip:
// sides + bottom get a margin, the top gets extra headroom for stacked lanes.
export const ROOM_PAD_X = 60;
export const ROOM_PAD_TOP = 200;
export const ROOM_PAD_BOT = 70;

export function canvasDimsFor(roomSize: number): { w: number; h: number } {
  return { w: roomSize + ROOM_PAD_X * 2, h: roomSize + ROOM_PAD_TOP + ROOM_PAD_BOT };
}

export interface DrawNpc {
  npc: Npc;
  x: number;
  y: number;
  dir: Direction;
  moving: boolean;
  walkPhase: number; // radians, advances while moving
  talking: boolean;
  dimmed: boolean; // on cooldown drift-apart
}

const BED_BLANKETS = ['#4cc9f0', '#f72585', '#80ed99', '#ff9e00'];

// Bubble anchor for a conversation: over the CURRENT speaker's head,
// switching sides as the turn passes. Shared by render + click hit-test
// so clicking a bubble always hits the visible one.
export interface ConvoState {
  aId: string;
  bId: string;
  lines: Array<{ speaker: string }>;
  cursor: number;
}

export function convoAnchor(
  c: ConvoState,
  runtime: RuntimeNpc[],
  byId: Map<string, Npc>,
): { x: number; y: number } {
  const ra = runtime.find((r) => r.npcId === c.aId);
  const rb = runtime.find((r) => r.npcId === c.bId);
  const full = (n: Npc | undefined): string => (n ? `${n.firstName} ${n.lastName}` : '');
  return speakerAnchor({
    speaker: c.lines[c.cursor]?.speaker ?? '',
    aName: full(byId.get(c.aId)),
    bName: full(byId.get(c.bId)),
    ax: ra?.x ?? 0,
    ay: ra?.y ?? 0,
    bx: rb?.x ?? 0,
    by: rb?.y ?? 0,
  });
}
export function drawBeds(
  ctx: CanvasRenderingContext2D,
  beds: Bed[],
  occupied: Set<string>,
): void {
  beds.forEach((b, i) => {
    const isOcc = occupied.has(b.id);
    ctx.save();
    ctx.globalAlpha = isOcc ? 0.95 : 1;
    // Frame.
    ctx.fillStyle = '#6b4f35';
    ctx.fillRect(b.x - 22, b.y - 16, 44, 32);
    // Mattress.
    ctx.fillStyle = isOcc ? '#b8a888' : '#d9c9a8';
    ctx.fillRect(b.x - 18, b.y - 12, 36, 24);
    // Pillow (head end).
    ctx.fillStyle = '#f4f1e8';
    ctx.fillRect(b.x - 18, b.y - 12, 10, 24);
    // Blanket (foot half).
    ctx.fillStyle = BED_BLANKETS[i % BED_BLANKETS.length];
    ctx.fillRect(b.x + 2, b.y - 12, 16, 24);
    if (isOcc) {
      ctx.fillStyle = '#eef1ff';
      ctx.font = 'bold 12px system-ui';
      ctx.textAlign = 'center';
      ctx.fillText('Z z', b.x, b.y - 22);
    }
    ctx.restore();
  });
}

export function drawRoom(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  wall: number,
): void {
  // Floor: warm wood planks.
  ctx.fillStyle = '#2a2118';
  ctx.fillRect(0, 0, w, h);
  ctx.fillStyle = '#241c14';
  for (let y = 0; y < h; y += 44) ctx.fillRect(0, y, w, 2);
  // Subtle vignette grid for depth perception.
  ctx.strokeStyle = 'rgba(255,255,255,0.03)';
  for (let x = 0; x < w; x += 88) {
    ctx.beginPath();
    ctx.moveTo(x, 0);
    ctx.lineTo(x, h);
    ctx.stroke();
  }
  // Walls: thick rounded border.
  ctx.fillStyle = '#4a3b2c';
  ctx.fillRect(0, 0, w, wall);
  ctx.fillRect(0, h - wall, w, wall);
  ctx.fillRect(0, 0, wall, h);
  ctx.fillRect(w - wall, 0, wall, h);
  ctx.fillStyle = '#5d4b38';
  ctx.fillRect(0, 0, w, 6);
}

export function drawNpc(ctx: CanvasRenderingContext2D, d: DrawNpc): void {
  const { npc, x, y, dir, moving, walkPhase } = d;
  const bob = moving ? Math.sin(walkPhase) * 2 : 0;
  ctx.save();
  ctx.globalAlpha = d.dimmed ? 0.75 : 1;
  // Shadow.
  ctx.fillStyle = 'rgba(0,0,0,0.35)';
  ctx.beginPath();
  ctx.ellipse(x, y + 20, 12, 5, 0, 0, Math.PI * 2);
  ctx.fill();
  const p = npc.palette;
  // Legs (pants) with tiny walk alternate.
  const legSwing = moving ? Math.sin(walkPhase) * 3 : 0;
  ctx.fillStyle = p.pants;
  ctx.fillRect(x - 7, y + 8 + bob * 0.3, 6, 12 - legSwing * 0.3);
  ctx.fillRect(x + 1, y + 8 + bob * 0.3, 6, 12 + legSwing * 0.3);
  // Body (shirt).
  ctx.fillStyle = p.shirt;
  ctx.fillRect(x - 9, y - 6 + bob, 18, 16);
  // Arms.
  ctx.fillRect(x - 12, y - 4 + bob - legSwing * 0.4, 4, 12);
  ctx.fillRect(x + 8, y - 4 + bob + legSwing * 0.4, 4, 12);
  // Head (skin).
  ctx.fillStyle = p.skin;
  ctx.beginPath();
  ctx.arc(x, y - 14 + bob, 9, 0, Math.PI * 2);
  ctx.fill();
  // Hair cap.
  ctx.fillStyle = p.hair;
  ctx.beginPath();
  ctx.arc(x, y - 16 + bob, 9, Math.PI, 0);
  ctx.fill();
  // Facing eyes (4-dir).
  ctx.fillStyle = '#1a1a1a';
  const ex = dir === 'left' ? -3 : dir === 'right' ? 3 : 0;
  const ey = dir === 'up' ? -1 : dir === 'down' ? 2 : 1;
  ctx.fillRect(x - 4 + ex, y - 14 + bob + ey, 2, 3);
  ctx.fillRect(x + 2 + ex, y - 14 + bob + ey, 2, 3);
  // Talking ring.
  if (d.talking) {
    ctx.strokeStyle = '#ffd166';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(x, y - 4, 22, 0, Math.PI * 2);
    ctx.stroke();
  }
  // Name label + provenance diamond (gold = LLM-made, grey = offline).
  ctx.font = '11px system-ui';
  ctx.textAlign = 'center';
  ctx.fillStyle = 'rgba(0,0,0,0.6)';
  const label = `${npc.firstName} ${npc.lastName}`;
  const w = ctx.measureText(label).width + 10;
  ctx.fillRect(x - w / 2, y + 24, w, 16);
  ctx.fillStyle = '#fff';
  ctx.fillText(label, x, y + 36);
  ctx.fillStyle = npc.origin === 'llm' ? '#06d6a0' : npc.origin === 'human' ? '#ffd166' : '#8a93a8';
  const dx = x + w / 2 + 7;
  const dy = y + 32;
  ctx.beginPath();
  ctx.moveTo(dx, dy - 4);
  ctx.lineTo(dx + 4, dy);
  ctx.lineTo(dx, dy + 4);
  ctx.lineTo(dx - 4, dy);
  ctx.closePath();
  ctx.fill();
  ctx.restore();
}

export interface DrawBubble {
  x: number; // bubble center (already edge-nudged by caller)
  y: number;
  color: string;
  speaker: string;
  text: string; // already typewriter-revealed prefix
  tailX?: number; // world x the tail points at (speaker head); defaults to x
  alpha?: number;
}

export interface Floater {
  x: number;
  y: number;
  items: Array<{ label: string; up: boolean }>;
  ageS: number; // seconds since conversation ended
  lifeS: number;
}

// Post-chat +/- indicators: rise slowly above the NPC's head, fade at the end.
export function drawFloaters(ctx: CanvasRenderingContext2D, f: Floater): void {
  const t = Math.min(1, f.ageS / f.lifeS);
  const alpha = t < 0.7 ? 1 : 1 - (t - 0.7) / 0.3;
  const rise = t * 30;
  ctx.save();
  ctx.globalAlpha = Math.max(0, alpha);
  ctx.font = 'bold 12px system-ui';
  ctx.textAlign = 'left';
  f.items.forEach((it, i) => {
    const y = f.y - 64 - rise - i * 18;
    const wpx = ctx.measureText(it.label).width + 16;
    ctx.fillStyle = 'rgba(10,12,20,0.85)';
    ctx.fillRect(f.x - wpx / 2, y - 13, wpx, 17);
    ctx.fillStyle = it.up ? '#06d6a0' : '#ff5d8f';
    ctx.fillText(it.label, f.x - wpx / 2 + 8, y);
  });
  ctx.restore();
}

// Word-wrap shared by draw + measure so edge-nudging matches rendering.
export const BUBBLE_MAX_W = 220;
const BUBBLE_NAME_H = 16; // speaker header height; measure + draw must agree

export function wrapBubbleText(ctx: CanvasRenderingContext2D, text: string): string[] {
  ctx.font = '13px system-ui';
  const words = text.split(/\s+/);
  const lines: string[] = [];
  let cur = '';
  for (const w of words) {
    const t = cur ? cur + ' ' + w : w;
    if (ctx.measureText(t).width > BUBBLE_MAX_W && cur) {
      lines.push(cur);
      cur = w;
    } else cur = t;
  }
  if (cur) lines.push(cur);
  return lines;
}

// Bubble box for already-wrapped lines. Caller clamps x via clampBubbleX.
export function measureBubble(ctx: CanvasRenderingContext2D, lines: string[]): { w: number; h: number } {
  ctx.font = '13px system-ui';
  return {
    w: Math.min(240, Math.max(120, ...lines.map((l) => ctx.measureText(l).width)) + 20),
    h: BUBBLE_NAME_H + lines.length * 17 + 18,
  };
}

// Thinking pill for claimed-but-waiting pairs (script still generating).
// Small, grey, above the pair midpoint — reads as "thinking", not frozen.
export function drawWaiting(ctx: CanvasRenderingContext2D, x: number, y: number): void {
  ctx.save();
  ctx.fillStyle = '#fff';
  ctx.strokeStyle = '#8a93a8';
  ctx.lineWidth = 2;
  const wpx = 40;
  const h = 22;
  const bx = Math.round(x - wpx / 2);
  const by = Math.round(y - 52 - h);
  roundRect(ctx, bx, by, wpx, h, 10);
  ctx.fill();
  ctx.stroke();
  ctx.fillStyle = '#8a93a8';
  ctx.font = 'bold 13px system-ui';
  ctx.textAlign = 'center';
  ctx.fillText('…', x, by + 16);
  ctx.restore();
}

// Speech bubble with colored per-convo border (non-overlap via layout lanes).
export function drawBubble(ctx: CanvasRenderingContext2D, b: DrawBubble): void {
  ctx.save();
  if (b.alpha != null) ctx.globalAlpha = b.alpha;
  const lines = wrapBubbleText(ctx, b.text);
  const { w: wpx, h } = measureBubble(ctx, lines);
  const bx = Math.round(b.x - wpx / 2);
  const by = Math.round(b.y - h);
  const tailX = b.tailX ?? b.x;
  // Tail.
  ctx.fillStyle = '#fff';
  ctx.strokeStyle = b.color;
  ctx.lineWidth = 3;
  roundRect(ctx, bx, by, wpx, h, 10);
  ctx.fill();
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(tailX - 6, by + h);
  ctx.lineTo(tailX + 6, by + h);
  ctx.lineTo(tailX, by + h + 10);
  ctx.closePath();
  ctx.fill();
  // Speaker.
  ctx.fillStyle = b.color;
  ctx.font = 'bold 11px system-ui';
  ctx.textAlign = 'left';
  ctx.fillText(b.speaker, bx + 10, by + 16);
  // Body.
  ctx.fillStyle = '#1a1a2e';
  ctx.font = '13px system-ui';
  lines.forEach((l, i) => ctx.fillText(l, bx + 10, by + BUBBLE_NAME_H + 18 + i * 17));
  ctx.restore();
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number, h: number, r: number,
): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}
